
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { User, AssetCategory } from '../types';
import { api } from '../services/api';
import { Plus, Loader2, Trash2, Edit2, Check, X } from 'lucide-react';

interface AssetCategoriesViewProps {
    user: User;
}

export const AssetCategoriesView: React.FC<AssetCategoriesViewProps> = ({ user }) => {
    const { t } = useTranslation('asset_categories');
    const isAssetManager = user.memberProfile?.teamRole === 'asset_manager';
    const [categories, setCategories] = useState<AssetCategory[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isCreating, setIsCreating] = useState(false);

    // New Category Form State
    const [newCategory, setNewCategory] = useState({
        title: '',
        code: '',
        depreciationRate: 0,
        usefulLife: 0,
        method: 'fixed' as 'fixed' | 'declining',
        parentId: ''
    });

    // Editing State
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editForm, setEditForm] = useState({
        title: '',
        code: '',
        depreciationRate: 0,
        usefulLife: 0,
        method: 'fixed' as 'fixed' | 'declining',
        parentId: ''
    });

    // Bulk Actions State
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        loadData();
    }, [user.id]);

    const loadData = async () => {
        setIsLoading(true);
        try {
            const agencyId = user.agencyId || user.id;
            const data = await api.getAssetCategories(agencyId);
            setCategories(data);
            setSelectedIds(new Set()); // clear selection on load
        } catch (e) {
            console.error("Failed to load asset categories", e);
        } finally {
            setIsLoading(false);
        }
    };

    const toggleSelect = (id: string) => {
        const newSet = new Set(selectedIds);
        if (newSet.has(id)) newSet.delete(id);
        else newSet.add(id);
        setSelectedIds(newSet);
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === categories.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(categories.map(c => c.id)));
        }
    };

    const handleBulkDelete = async () => {
        if (!window.confirm(t('actions.confirm_delete_multiple', 'Are you sure you want to delete the selected items?'))) return;
        setIsDeleting(true);
        try {
            const ids = Array.from(selectedIds);
            for (const id of ids) {
                await api.deleteAssetCategory(id);
            }
            setSelectedIds(new Set());
            await loadData();
        } catch (e: any) {
            alert(e.message || 'Failed to delete selected items');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsCreating(true);
        try {
            const agencyId = user.agencyId || user.id;
            await api.createAssetCategory(agencyId, newCategory);
            await loadData();
            setNewCategory({
                title: '',
                code: '',
                depreciationRate: 0,
                usefulLife: 0,
                method: 'fixed',
                parentId: ''
            });
        } catch (e: any) {
            alert(e.message || 'Failed to create category');
        } finally {
            setIsCreating(false);
        }
    };

    const startEditing = (category: AssetCategory) => {
        setEditingId(category.id);
        setEditForm({
            title: category.title,
            code: category.code,
            depreciationRate: category.depreciationRate,
            usefulLife: category.usefulLife,
            method: category.method,
            parentId: category.parentId || ''
        });
    };

    const cancelEditing = () => {
        setEditingId(null);
        setEditForm({
            title: '',
            code: '',
            depreciationRate: 0,
            usefulLife: 0,
            method: 'fixed',
            parentId: ''
        });
    };

    const handleUpdate = async (id: string) => {
        try {
            await api.updateAssetCategory(id, editForm);
            setEditingId(null);
            loadData();
        } catch (e: any) {
            alert(e.message || 'Failed to update');
        }
    };

    const handleDelete = async (id: string) => {
        if (window.confirm(t('actions.confirm_delete'))) {
            try {
                await api.deleteAssetCategory(id);
                loadData();
            } catch (e: any) {
                alert(e.message || 'Failed to delete');
            }
        }
    };

    return (
        <div className="space-y-8">
            {/* Header / Create Section */}
            {/* Header / Create Section */}
            {!isAssetManager && (
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
                    <h2 className="text-xl font-bold text-slate-900 mb-6">{t('title')}</h2>
                    <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 items-end">
                        <div className="lg:col-span-2">
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('form.title')}</label>
                            <input
                                required
                                type="text"
                                value={newCategory.title}
                                onChange={e => setNewCategory({ ...newCategory, title: e.target.value })}
                                className="w-full border border-slate-300 rounded-xl p-2.5 outline-none focus:ring-2 focus:ring-black"
                                placeholder={t('placeholders.title')}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('form.code')}</label>
                            <input
                                required
                                type="text"
                                value={newCategory.code}
                                onChange={e => setNewCategory({ ...newCategory, code: e.target.value })}
                                className="w-full border border-slate-300 rounded-xl p-2.5 outline-none focus:ring-2 focus:ring-black"
                                placeholder={t('placeholders.code')}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('form.depreciation_rate')}</label>
                            <input
                                required
                                type="number"
                                min="0"
                                max="100"
                                value={newCategory.depreciationRate}
                                onChange={e => setNewCategory({ ...newCategory, depreciationRate: parseFloat(e.target.value) })}
                                className="w-full border border-slate-300 rounded-xl p-2.5 outline-none focus:ring-2 focus:ring-black"
                                placeholder={t('placeholders.depreciation_rate')}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('form.useful_life')}</label>
                            <input
                                required
                                type="number"
                                min="0"
                                value={newCategory.usefulLife}
                                onChange={e => setNewCategory({ ...newCategory, usefulLife: parseFloat(e.target.value) })}
                                className="w-full border border-slate-300 rounded-xl p-2.5 outline-none focus:ring-2 focus:ring-black"
                                placeholder={t('placeholders.useful_life')}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('form.method')}</label>
                            <select
                                value={newCategory.method}
                                onChange={e => setNewCategory({ ...newCategory, method: e.target.value as any })}
                                className="w-full border border-slate-300 rounded-xl p-2.5 outline-none focus:ring-2 focus:ring-black bg-white"
                            >
                                <option value="fixed">{t('form.methods.fixed')}</option>
                                <option value="declining">{t('form.methods.declining')}</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('form.parent_category')}</label>
                            <select
                                value={newCategory.parentId}
                                onChange={e => setNewCategory({ ...newCategory, parentId: e.target.value })}
                                className="w-full border border-slate-300 rounded-xl p-2.5 outline-none focus:ring-2 focus:ring-black bg-white"
                            >
                                <option value="">{t('form.no_parent')}</option>
                                {categories.map(c => (
                                    <option key={c.id} value={c.id}>{c.title}</option>
                                ))}
                            </select>
                        </div>

                        <div className="lg:col-span-full flex justify-end">
                            <button disabled={isCreating} type="submit" className="bg-black text-white px-6 py-2.5 rounded-xl font-bold hover:opacity-80 transition flex items-center justify-center gap-2">
                                {isCreating ? <Loader2 className="animate-spin" size={18} /> : <Plus size={18} />}
                                {t('form.add')}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* List */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
                    <h3 className="font-bold text-slate-900">{t('list_title')}</h3>

                    {!isAssetManager && selectedIds.size > 0 && (
                        <div className="flex items-center gap-4">
                            <span className="text-sm text-slate-500 font-medium">
                                {selectedIds.size} {t('selected', 'selected')}
                            </span>
                            <button
                                onClick={handleBulkDelete}
                                disabled={isDeleting}
                                className="flex items-center gap-2 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-sm font-bold hover:bg-red-100 transition disabled:opacity-50"
                            >
                                {isDeleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                                {t('actions.delete_selected', 'Delete Selected')}
                            </button>
                        </div>
                    )}
                </div>
                {isLoading ? (
                    <div className="p-12 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>
                ) : (
                    <table className="w-full text-left text-sm">
                        <thead className="bg-slate-50 text-slate-500">
                            <tr>
                                {!isAssetManager && (
                                    <th className="px-6 py-4 w-12">
                                        <input
                                            type="checkbox"
                                            checked={categories.length > 0 && selectedIds.size === categories.length}
                                            onChange={toggleSelectAll}
                                            className="rounded border-slate-300 text-black focus:ring-black"
                                        />
                                    </th>
                                )}
                                <th className="px-6 py-4">{t('table.title')}</th>
                                <th className="px-6 py-4">{t('table.code')}</th>
                                <th className="px-6 py-4">{t('table.depreciation_rate')} (%)</th>
                                <th className="px-6 py-4">{t('table.useful_life')} (Y)</th>
                                <th className="px-6 py-4">{t('table.method')}</th>
                                {!isAssetManager && <th className="px-6 py-4 text-right">{t('table.actions')}</th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {categories.length === 0 ? (
                                <tr><td colSpan={!isAssetManager ? 7 : 5} className="px-6 py-8 text-center text-slate-400">{t('no_categories')}</td></tr>
                            ) : (() => {
                                // Sort categories hierarchically
                                // Roots: No parentId or parentId not in list (orphan safety)
                                const parentIds = new Set(categories.map(c => c.id));
                                const roots = categories.filter(c => !c.parentId || !parentIds.has(c.parentId))
                                    .sort((a, b) => a.title.localeCompare(b.title));

                                const flattened: AssetCategory[] = [];
                                roots.forEach(root => {
                                    flattened.push(root);
                                    const children = categories.filter(c => c.parentId === root.id)
                                        .sort((a, b) => a.title.localeCompare(b.title));
                                    flattened.push(...children);
                                });

                                return flattened.map(c => (
                                    <tr key={c.id} className="hover:bg-slate-50">
                                        {editingId === c.id ? (
                                            <>
                                                {!isAssetManager && <td className="px-6 py-4"></td>}
                                                <td className="px-6 py-4">
                                                    <input
                                                        type="text"
                                                        value={editForm.title}
                                                        onChange={e => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                                                        className="w-full border border-slate-300 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                </td>
                                                <td className="px-6 py-4">
                                                    <input
                                                        type="text"
                                                        value={editForm.code}
                                                        onChange={e => setEditForm(prev => ({ ...prev, code: e.target.value }))}
                                                        className="w-full border border-slate-300 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                                                    />
                                                </td>
                                                <td className="px-6 py-4">
                                                    <input
                                                        type="number"
                                                        value={editForm.depreciationRate}
                                                        onChange={e => setEditForm(prev => ({ ...prev, depreciationRate: parseFloat(e.target.value) }))}
                                                        className="w-20 border border-slate-300 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                </td>
                                                <td className="px-6 py-4">
                                                    <input
                                                        type="number"
                                                        value={editForm.usefulLife}
                                                        onChange={e => setEditForm(prev => ({ ...prev, usefulLife: parseFloat(e.target.value) }))}
                                                        className="w-20 border border-slate-300 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                </td>
                                                <td className="px-6 py-4">
                                                    <select
                                                        value={editForm.method}
                                                        onChange={e => setEditForm(prev => ({ ...prev, method: e.target.value as any }))}
                                                        className="border border-slate-300 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                                    >
                                                        <option value="fixed">{t('form.methods.fixed')}</option>
                                                        <option value="declining">{t('form.methods.declining')}</option>
                                                    </select>
                                                    <div className="mt-2">
                                                        <select
                                                            value={editForm.parentId}
                                                            onChange={e => setEditForm(prev => ({ ...prev, parentId: e.target.value }))}
                                                            className="w-full border border-slate-300 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                                        >
                                                            <option value="">{t('form.no_parent')}</option>
                                                            {categories.filter(cat => cat.id !== c.id).map(cat => (
                                                                <option key={cat.id} value={cat.id}>{cat.title}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </td>
                                                {!isAssetManager && (
                                                    <td className="px-6 py-4 text-right flex justify-end gap-2">
                                                        <button onClick={() => handleUpdate(c.id)} className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition" title={t('actions.save')}>
                                                            <Check size={16} />
                                                        </button>
                                                        <button onClick={cancelEditing} className="p-2 bg-slate-100 text-slate-500 rounded-lg hover:bg-slate-200 transition" title={t('actions.cancel')}>
                                                            <X size={16} />
                                                        </button>
                                                    </td>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                {!isAssetManager && (
                                                    <td className="px-6 py-4">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedIds.has(c.id)}
                                                            onChange={() => toggleSelect(c.id)}
                                                            className="rounded border-slate-300 text-black focus:ring-black"
                                                        />
                                                    </td>
                                                )}
                                                <td className="px-6 py-4 font-bold text-slate-900">
                                                    <div style={{ paddingLeft: c.parentId ? '20px' : '0px' }}>
                                                        {c.parentId && <span className="text-slate-400 mr-2">↳</span>}
                                                        {c.title}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 font-mono text-slate-600">{c.code}</td>
                                                <td className="px-6 py-4 text-slate-600">{c.depreciationRate}%</td>
                                                <td className="px-6 py-4 text-slate-600">{c.usefulLife}</td>
                                                <td className="px-6 py-4 text-slate-600">
                                                    <span className={`px-2 py-1 rounded text-xs font-bold ${c.method === 'fixed' ? 'bg-blue-50 text-blue-700' : 'bg-orange-50 text-orange-700'}`}>
                                                        {t(`form.methods.${c.method}`)}
                                                    </span>
                                                </td>
                                                {!isAssetManager && (
                                                    <td className="px-6 py-4 text-right flex justify-end gap-2">
                                                        <button onClick={() => startEditing(c)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition" title={t('actions.edit')}>
                                                            <Edit2 size={16} />
                                                        </button>
                                                        <button onClick={() => handleDelete(c.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition" title={t('actions.delete')}>
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </td>
                                                )}
                                            </>
                                        )}
                                    </tr>
                                ))
                            })()}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};
